import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CircleDashed, Mars, ShieldQuestion, Venus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  GENDER_OPTIONS,
  type GenderOption,
} from "@/features/settings/profile/utils/gender";
import { normalizeGender } from "@/features/settings/profile/utils/gender";
import useAuthStore from "@/store/use-auth-store";
import { usePersonaSetup } from "../hooks/use-persona-setup";
import { QuestCard } from "../components/QuestCard";

const GENDER_ICONS: Record<
  GenderOption,
  ComponentType<{ className?: string }>
> = {
  male: Mars,
  female: Venus,
  other: CircleDashed,
  prefer_not_to_say: ShieldQuestion,
};

export const GenderQuest: React.FC = () => {
  const { t } = useTranslation();
  const { saveFields, completeQuest, isSaving } = usePersonaSetup();
  const selected = normalizeGender(useAuthStore((state) => state.user?.gender));

  return (
    <QuestCard>
      <div className="mb-5 flex flex-col gap-1">
        <h2 className="text-xl font-bold text-text-primary dark:text-gray-100 sm:text-2xl">
          {t("personaSetup.quests.gender.title")}
        </h2>
        <p className="text-sm text-text-tertiary dark:text-gray-400">
          {t("personaSetup.quests.gender.description")}
        </p>
      </div>

      <div
        className="grid grid-cols-2 gap-2.5 sm:gap-3"
        role="radiogroup"
        aria-label={t("settings.profile.gender")}
      >
        {GENDER_OPTIONS.map((option) => {
          const Icon = GENDER_ICONS[option];

          return (
            <button
              key={option}
              type="button"
              role="radio"
              disabled={isSaving}
              onClick={async () => {
                if (await saveFields({ gender: option })) {
                  completeQuest("gender");
                }
              }}
              className={cn(
                "flex min-h-[6rem] flex-col items-start gap-2 rounded-2xl border p-3.5 text-left transition-all sm:min-h-0 sm:p-4",
                selected === option
                  ? "border-text-brand bg-indigo-50/70 ring-2 ring-text-brand/25 dark:border-indigo-400 dark:bg-indigo-500/15"
                  : "border-border-secondary bg-white hover:border-text-brand/50 dark:border-gray-600 dark:bg-gray-900/60 dark:hover:border-indigo-400/60"
              )}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-text-brand dark:bg-indigo-500/20 dark:text-indigo-300">
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold text-text-primary dark:text-gray-100">
                {t(`settings.profile.genderOptions.${option}`)}
              </span>
              <span className="hidden text-xs text-text-tertiary dark:text-gray-400 sm:block">
                {t(`settings.profile.genderHints.${option}`)}
              </span>
            </button>
          );
        })}
      </div>
    </QuestCard>
  );
};
